<script setup lang="ts">
import {
  X,
  Check,
  Shield02,
  File06,
  AlertCircle,
  MessageTextSquare02,
  Building07,
} from "utruckin-icons";
import { computed, onBeforeUnmount, onUnmounted, ref, watch } from "vue";
import CustomTab from "@/components/custom/CustomTab.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import { useNotificationStore } from "@/features/notification/notification.store";
import { INotificationItem } from "@/features/notification/notification.types";
import { DateTime } from "luxon";

type CloseReason = "overlay" | "esc" | "button" | "program";

const notificationStore = useNotificationStore();

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    title?: string;
    closeOnOverlay?: boolean;
    closeOnEsc?: boolean;
    lockScroll?: boolean;
  }>(),
  {
    closeOnOverlay: true,
    closeOnEsc: true,
    lockScroll: true,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", v: boolean): void;
  (e: "open"): void;
  (e: "close", reason: CloseReason): void;
}>();

const notificationLoading = ref<boolean>(false);
const notificationItems = ref<INotificationItem[]>([]);
const modalBody = ref<HTMLElement | null>(null);
const loadMoreTrigger = ref<HTMLElement | null>(null);
const hasMore = ref<boolean>(true);

const isOpen = computed(() => props.modelValue);

let mouseDownOnOverlay = false;
let observer: IntersectionObserver | null = null;

function setBodyLock(locked: boolean) {
  if (!props.lockScroll) return;
  document.body.style.overflow = locked ? "hidden" : "";
}

function close(reason: CloseReason = "program") {
  emit("close", reason);
  emit("update:modelValue", false);
}

function onOverlayMouseDown(e: MouseEvent) {
  mouseDownOnOverlay = e.target === e.currentTarget;
}

function onOverlayMouseUp() {
  if (props.closeOnOverlay && mouseDownOnOverlay) {
    close("overlay");
  }
  mouseDownOnOverlay = false;
}

function onKeydown(e: KeyboardEvent) {
  if (!props.closeOnEsc) return;
  if (e.key === "Escape" && isOpen.value) close("esc");
}

const closeModal = () => {
  close("overlay");
};

const clickNotification = async (item: INotificationItem) => {
  if (item.read_time) return;
  await notificationStore.notificationRead(
    notificationLoading,
    item.id,
    async (data: any) => {
      if (data) {
        await reload();

        await notificationStore.getCount(notificationLoading, (data: any) => {
          notificationStore.notificationCount = data.count;
        });
      }
    }
  );
};

const getList = async () => {
  if (notificationLoading.value || !hasMore.value) return;
  await notificationStore.getList(notificationLoading, (data: any) => {
    notificationItems.value.push(...data.items);
    const meta = data.meta;
    hasMore.value = meta?.page * meta.per_page < meta.total;

    notificationStore.notificationFilter.page =
      (notificationStore.notificationFilter.page as number) + 1;
  });
};

const reload = async () => {
  notificationItems.value = [];
  hasMore.value = true;
  notificationStore.notificationFilter.page = 1;

  await getList();
};

const utcIsoToZoneRelative = (value: string) =>
  DateTime.fromISO(value, { zone: "utc" })
    .setZone("America/New_York")
    .toRelative()!;

const readAll = async () => {
  await notificationStore.readAll(notificationLoading, async (data: any) => {
    if (data) {
      await reload();

      await notificationStore.getCount(notificationLoading, (data: any) => {
        notificationStore.notificationCount = data.count;
      });
    }
  });
};

watch(
  isOpen,
  async (v) => {
    if (v) {
      emit("open");
      setBodyLock(true);
      window.addEventListener("keydown", onKeydown);
      notificationItems.value = [];
      hasMore.value = true;
      notificationStore.notificationFilter.page = 1;

      await getList();

      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            getList();
          }
        },
        {
          root: modalBody.value,
          rootMargin: "200px",
          threshold: 0,
        }
      );

      if (loadMoreTrigger.value) {
        observer.observe(loadMoreTrigger.value);
      }
    } else {
      setBodyLock(false);
      window.removeEventListener("keydown", onKeydown);
      observer?.disconnect();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  setBodyLock(false);
  window.removeEventListener("keydown", onKeydown);
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="isOpen"
        class="modal-overlay"
        @mousedown="onOverlayMouseDown"
        @mouseup="onOverlayMouseUp"
      >
        <div
          class="modal-card"
          role="dialog"
          aria-modal="true"
          @mousedown.stop
          @mouseup.stop
        >
          <header class="modal-header">
            <h3 class="modal-title">All notifications</h3>

            <div class="modal-close" @click="closeModal">
              <X :size="24" />
            </div>
          </header>

          <div class="head__action">
            <CommonButton
              type="secondary"
              label="Read all"
              prepend
              :prependIcon="Check"
              @clicked="readAll"
            />
          </div>

          <div class="modal-body" ref="modalBody">
            <div
              v-for="item in notificationItems"
              class="notification__item"
              :key="item.id"
              @click="clickNotification(item)"
            >
              <div class="icon" :class="item.type">
                <File06 v-if="item.type == 'task_assigned'" :size="20" />
                <AlertCircle v-else-if="item.type == 'qa_failed'" :size="20" />
                <MessageTextSquare02
                  v-else-if="item.type == 'task_comment_added'"
                  :size="20"
                />
                <Building07
                  v-else-if="item.type == 'company_created_without_team'"
                  :size="20"
                />
                <Shield02 v-else :size="20" />
              </div>
              <div class="body">
                <div class="body__header">
                  <div class="headline">
                    <div
                      v-if="!item.read_time"
                      class="body__header--status"
                    ></div>
                    <div class="body__header--title">{{ item.title }}</div>
                  </div>
                  <div class="body__header--time">
                    {{ utcIsoToZoneRelative(item.created_time) }}
                  </div>
                </div>

                <div class="body__main">
                  {{ item.body }}
                </div>
              </div>
            </div>

            <div ref="loadMoreTrigger" />
          </div>

          <footer v-if="$slots.footer" class="modal-footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: end;
  padding: 12px;
  z-index: 9999;
}

.modal-card {
  width: min(440px, 100%);
  height: 100%;
  background: var(--color-system-background);
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  /* overflow: hidden убрано — чтобы dropdown не обрезался */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--color-label);
}

.modal-title {
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
}

.modal-close {
  cursor: pointer;
}

.head__action {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 0;
  border-bottom: 1px solid var(--color-border);
}

.m-x {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-white);
  cursor: pointer;
  font-size: 20px;
  line-height: 0;
}

.modal-body {
  margin-top: 16px;
  overflow: auto;
  height: 100%;

  .notification__item {
    background: var(--color-secondary-system-background);
    border: 1px solid var(--color-border);
    border-radius: 14px;
    padding: 12px;
    display: flex;
    gap: 12px;
    cursor: pointer;
    margin-bottom: 8px;

    .icon {
      background: var(--color-system-background);
      border: 1px solid var(--color-blue-light);
      border-radius: 100px;
      min-width: 44px;
      height: 44px;
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--color-primary);

      &.task_assigned {
        color: var(--color-system-green);
        border-color: var(--color-green-light);
      }

      &.qa_failed {
        color: var(--color-system-red);
        border-color: var(--color-red-light);
      }
    }

    .body {
      flex: 1;

      &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 4px;

        .headline {
          display: flex;
          align-items: center;
        }

        &--status {
          width: 8px;
          height: 8px;
          margin-right: 8px;
          background: var(--color-primary);
          border-radius: 50%;
        }

        &--title {
          font-size: 16px;
          line-height: 24px;
          font-weight: 500;
          color: var(--color-label);
        }

        &--time {
          font-size: 12px;
          line-height: 20px;
          color: var(--color-label-secondary);
        }
      }

      &__main {
        font-size: 14px;
        line-height: 18px;
        font-weight: 400;
        color: var(--color-label-secondary);
      }
    }
  }
}

.modal-footer {
  padding: 14px 0;
  margin: 0 16px;
  border-top: 1px solid var(--color-border);
  display: flex;
  gap: 10px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
